import React, { useCallback, useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";
import { FieldControl, FormBuilder } from "react-reactive-form";
import { useCsrDropdownData } from "../hooks/useCsrDropdownData";
import { buildSelectStyles } from "../utils/selectStyles";

const DEFAULT_PLACEHOLDER = "Type to search from list or select from the list";
const DEFAULT_NO_OPTIONS = "No matches found";
const DEFAULT_LOADING = "Loading data...";

const normalizeValue = value => {
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    return normalizeValue(value[0]);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return "";
    if (trimmed.toLowerCase() === "false") return "";
    return trimmed;
  }

  if (value === false || value === null || value === undefined) return "";

  return String(value).trim();
};

const formatNames = names => {
  if (!Array.isArray(names)) return [];
  return names
    .filter(item => item && typeof item === "string")
    .map(item => ({ value: item, label: item }));
};

const filterOptions = (options, inputValue) => {
  if (!inputValue) return options;
  const search = inputValue.toLowerCase();
  return options.filter(option => option.label.toLowerCase().includes(search));
};

// Shared react-select styling — see utils/selectStyles.js.
const customStyles = disabled => buildSelectStyles({ isDisabled: disabled });

/**
 * Single-select dropdown over table names. Pure Redux consumer — the
 * csrData slice is hydrated by startStudyDataLoad (orchestrated from
 * QuestionsProvider). The component performs no polling and no direct
 * API calls, so N instances on a page do not multiply backend traffic.
 */
const PlainTableSingleSelectDropdown = ({
  controlName,
  formGroup,
  disabled = false,
  // studyId accepted for API compatibility; data sourced from Redux.
  studyId, // eslint-disable-line no-unused-vars
  placeholder = DEFAULT_PLACEHOLDER,
  noOptionsMessage = DEFAULT_NO_OPTIONS,
  loadingMessage = DEFAULT_LOADING,
}) => {
  const { items, isLoading, isReady } = useCsrDropdownData({ kind: "table" });

  const options = useMemo(() => formatNames(items), [items]);

  const loadOptions = useCallback(
    (inputValue = "") => Promise.resolve(filterOptions(options, inputValue)),
    [options]
  );

  // Ensure a plain FormControl exists at controlName. Some legacy paths
  // initialised this as a FormArray (with .push) — migrate it to a control.
  useEffect(() => {
    if (!formGroup || typeof formGroup.contains !== "function") return;

    if (!formGroup.contains(controlName)) {
      formGroup.addControl(controlName, FormBuilder.control(""));
      return;
    }

    const existing = formGroup.get && formGroup.get(controlName);
    const looksLikeFormArray =
      existing &&
      typeof existing.push === "function" &&
      Array.isArray(existing.value);

    if (looksLikeFormArray && typeof formGroup.removeControl === "function") {
      formGroup.removeControl(controlName);
      formGroup.addControl(controlName, FormBuilder.control(""));
    }
  }, [formGroup, controlName]);

  // Keep the rendered selection in sync with external form mutations
  // (e.g. saved-state restoration after the /questions response lands).
  const [, forceRender] = useState(0);
  useEffect(() => {
    const formControl = formGroup?.get && formGroup.get(controlName);
    if (!formControl || !formControl.valueChanges) return;

    const subscription = formControl.valueChanges.subscribe(() => {
      forceRender(v => v + 1);
    });

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [formGroup, controlName]);

  return (
    <FieldControl
      name={controlName}
      render={({ value, handler }) => {
        void handler;
        const formControl = formGroup?.get && formGroup.get(controlName);
        if (!formControl) return null;

        const currentValue =
          value !== undefined && value !== null ? value : formControl.value;
        const normalizedValue = normalizeValue(currentValue);
        const selectedOption = normalizedValue
          ? { value: normalizedValue, label: normalizedValue }
          : null;

        return (
          <AsyncSelect
            // Force remount once Redux options arrive so AsyncSelect's
            // cached empty defaultOptions are replaced with the real list.
            key={`${controlName}-${options.length}`}
            isMulti={false}
            value={selectedOption}
            loadOptions={loadOptions}
            defaultOptions={options}
            isLoading={isLoading && !isReady}
            onChange={option => {
              const nextValue = option ? option.value : "";
              formControl.setValue(nextValue);
              if (formControl.markAsTouched) {
                formControl.markAsTouched();
              }
            }}
            styles={customStyles(disabled)}
            placeholder={placeholder}
            isClearable
            isSearchable
            isDisabled={disabled}
            className="plain-table-single-select"
            classNamePrefix="plain-table-single-select"
            noOptionsMessage={({ inputValue }) =>
              inputValue ? noOptionsMessage : placeholder
            }
            loadingMessage={() => loadingMessage}
            cacheOptions
          />
        );
      }}
    />
  );
};

export default PlainTableSingleSelectDropdown;
